import type { PaintSettings, WheelFinish, TireTreatment } from '../types/vehicle';

/**
 * Pure PBR parameter derivation.
 *
 * These functions decide *what a surface should look like*; the renderer only
 * applies the result to a material. Keeping the numbers here is what makes them
 * testable without a WebGL context, and it is what stops the paint rack and the
 * wheel bay from drifting apart as either one grows.
 */

export interface MaterialSpec {
  colorHex: string;
  metalness: number;
  roughness: number;
  clearcoat: number;
  clearcoatRoughness: number;
  /**
   * How strongly this surface samples scene.environment. Meaningless until an
   * environment probe is assigned, which is why it lives next to the finish
   * definition rather than in the renderer.
   */
  envMapIntensity: number;
  anisotropy: number;
  reflectivity: number;
}

/**
 * Polished finishes mirror the studio probe; satin finishes scatter it. Clamped
 * because a roughness outside 0..1 (possible from a bad preset) would otherwise
 * produce a negative intensity and a black surface.
 */
export function envMapIntensityForRoughness(roughness: number): number {
  const clamped = Math.min(1, Math.max(0, roughness));
  // Capped at the top: a near-mirror surface reflects the key softbox at full
  // strength, and an uncapped product clips the highlight to flat white.
  return Math.min(1.15, Math.max(0.35, 1.45 - clamped * 1.2));
}

export function derivePaintSpec(paint: PaintSettings): MaterialSpec {
  let metalness = 0.15;
  let roughness = Math.max(0.04, 0.88 - paint.clearcoatGloss * 0.82);
  let clearcoat = Math.max(0.8, paint.clearcoatGloss);
  let clearcoatRoughness = Math.max(0.02, (1 - paint.clearcoatGloss) * 0.3 + 0.03);
  let anisotropy = 0;

  if (paint.finishType === 'metallic_enamel') {
    // Flake density is what separates a Marina Blue from a solid enamel: it adds
    // metalness (the flake itself) without making the base coat mirror-bright.
    metalness = 0.75 + paint.metallicFleckDensity * 0.2;
    roughness = Math.max(0.08, 0.9 - paint.clearcoatGloss * 0.8);
    clearcoat = paint.clearcoatGloss;
  } else if (paint.finishType === 'satin_primer') {
    metalness = 0.05;
    roughness = Math.max(0.65, 0.95 - paint.clearcoatGloss * 0.4);
    clearcoat = 0.05;
    clearcoatRoughness = 0.8;
  }

  return {
    colorHex: paint.colorHex,
    metalness,
    roughness,
    clearcoat,
    clearcoatRoughness,
    envMapIntensity: envMapIntensityForRoughness(roughness),
    anisotropy,
    reflectivity: 0.9,
  };
}

const WHEEL_FINISH_PARAMS: Record<
  WheelFinish,
  { colorHex: string; metalness: number; roughness: number }
> = {
  polished: { colorHex: '#f3f4f6', metalness: 0.98, roughness: 0.05 },
  chrome: { colorHex: '#ffffff', metalness: 1.0, roughness: 0.02 },
  machined_gray: { colorHex: '#9ca3af', metalness: 0.85, roughness: 0.32 },
  matte_black: { colorHex: '#18181b', metalness: 0.25, roughness: 0.75 },
  gold: { colorHex: '#d4af37', metalness: 0.92, roughness: 0.22 },
};

export function deriveWheelSpec(finish: WheelFinish): MaterialSpec {
  const base = WHEEL_FINISH_PARAMS[finish];
  return {
    colorHex: base.colorHex,
    metalness: base.metalness,
    roughness: base.roughness,
    // Rims are metal: a thin clearcoat at most. Stacking clearcoat onto metalness
    // is what over-brightens a wheel into white plastic.
    clearcoat: base.metalness < 0.6 ? 0.1 : 0.18,
    clearcoatRoughness: Math.min(0.45, base.roughness * 0.8),
    envMapIntensity: envMapIntensityForRoughness(base.roughness),
    // Machined faces are radially brushed, so a little anisotropy sells the cut.
    anisotropy: 0.25,
    reflectivity: 0.9,
  };
}

const TYRE_SIDEWALL_HEX: Record<TireTreatment, string> = {
  blackwall: '#15171a',
  white_letter: '#d7d9dc',
  redline: '#7f1d1d',
  goldline: '#b08d3a',
};

export function deriveTireSpec(treatment: TireTreatment): MaterialSpec {
  return {
    colorHex: TYRE_SIDEWALL_HEX[treatment],
    metalness: 0.0,
    roughness: 0.92,
    clearcoat: 0.0,
    clearcoatRoughness: 1,
    // Rubber barely reflects a studio; over-lighting it looks wet.
    envMapIntensity: 0.25,
    anisotropy: 0,
    reflectivity: 0.15,
  };
}

export function deriveBrakeSpec(): MaterialSpec {
  return {
    colorHex: '#6b7280',
    metalness: 0.9,
    roughness: 0.42,
    clearcoat: 0,
    clearcoatRoughness: 1,
    envMapIntensity: 0.8,
    anisotropy: 0.4,
    reflectivity: 0.6,
  };
}

export function deriveGlassSpec(): MaterialSpec {
  return {
    colorHex: '#0b1116',
    metalness: 0.0,
    roughness: 0.08,
    clearcoat: 1.0,
    clearcoatRoughness: 0.04,
    // Glass is mostly a mirror of its surroundings, so it sits at the cap.
    envMapIntensity: 1.15,
    anisotropy: 0,
    reflectivity: 1.0,
  };
}